import React, { useState, useCallback, useRef } from 'react'
import { Button, Form, Table, Modal, Badge, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons'
import { useDropzone } from 'react-dropzone'
import { HslColorPicker, HslColor } from 'react-colorful'
import {
  useGetTagsQuery,
  useCreateTagMutation,
  useDeleteTagMutation,
  useUpdateTagMutation,
} from '../../../services/tagApi'
import { useAppSelector, useAnnotationStats } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { Tag } from '../../../types/models/tag'
import { generateRandomColor } from '../../../utils/helpers'
import ErrorDialog from '../../modals/ErrorDialog'

const TagManagementContainer = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .table {
    color: #e0e0e0;
    margin-bottom: 0;
    border-collapse: separate;
    border-spacing: 0;

    th {
      background: transparent;
      border: none;
      padding: 1rem 1.25rem;
      font-weight: 600;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      transition: color 0.3s ease;
      white-space: nowrap;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);

      &:hover {
        color: #fff;
      }

      i {
        margin-left: 0.5rem;
        font-size: 0.8rem;
      }
    }

    td {
      background: transparent;
      padding: 1rem 1.25rem;
      border: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      vertical-align: middle;
      font-size: 0.95rem;
      color: #e0e0e0;

      .tag-name {
        font-weight: 500;
        color: #fff;
      }

      .color-preview {
        display: inline-block;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        margin-right: 0.75rem;
        border: 2px solid rgba(255, 255, 255, 0.2);
        vertical-align: middle;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      .hex-color {
        color: rgba(255, 255, 255, 0.6);
        font-family: monospace;
        font-size: 0.9rem;
      }

      .badge {
        font-weight: 500;
        font-size: 0.85rem;
        padding: 0.4em 0.8em;
        background: rgba(102, 126, 234, 0.2);
        color: #e0e0e0;
        border: 1px solid rgba(102, 126, 234, 0.3);
        border-radius: 6px;
      }

      .date {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.9rem;
      }

      .btn-outline-danger {
        padding: 0.5rem 1rem;
        min-width: 100px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        border: 1px solid rgba(220, 53, 69, 0.5);
        color: #dc3545;
        background: transparent;
        transition: all 0.3s ease;
        border-radius: 8px;

        &:hover {
          background: rgba(220, 53, 69, 0.15);
          color: #ff4757;
          border-color: #dc3545;
          transform: translateY(-1px);
          box-shadow: 0 4px 15px rgba(220, 53, 69, 0.2);
        }

        i {
          font-size: 0.9rem;
        }
      }
    }

    tbody tr {
      background: transparent;
      transition: background-color 0.3s ease;

      &:hover {
        background-color: rgba(102, 126, 234, 0.1);
        td {
          background-color: transparent;
        }
      }
    }

    .form-check {
      margin: 0;

      .form-check-input {
        background-color: transparent;
        border-color: rgba(102, 126, 234, 0.3);
        cursor: pointer;

        &:checked {
          background-color: #667eea;
          border-color: #667eea;
        }

        &:focus {
          box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25);
        }
      }
    }
  }

  .header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    .search-box {
      flex: 1;
      max-width: 300px;
      margin-right: 1rem;

      .form-control {
        background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
        border: 1px solid rgba(102, 126, 234, 0.2);
        color: #fff;
        padding: 0.5rem 1rem;
        font-size: 0.95rem;
        border-radius: 8px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(10px);

        &:focus {
          border-color: rgba(102, 126, 234, 0.5);
          box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25), 0 8px 25px rgba(0, 0, 0, 0.3);
          transform: translateY(-1px);
        }

        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }

    .btn {
      padding: 0.5rem 1rem;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.3s ease;
      border-radius: 8px;

      &:hover {
        transform: translateY(-2px);
      }

      i,
      svg {
        font-size: 0.9rem;
      }
    }
  }
`

const TagContent = styled.div`
  background: transparent;
  padding: 1.5rem;
  border-radius: 0 0 16px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .header-actions {
    display: flex;
    gap: 1rem;
    align-items: center;
    margin-bottom: 1.5rem;

    .search-box {
      flex: 1;
      max-width: 300px;

      .form-control {
        background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
        border: 1px solid rgba(102, 126, 234, 0.2);
        color: #fff;
        padding: 0.6rem 1rem;
        font-size: 0.95rem;
        border-radius: 8px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(10px);

        &:focus {
          border-color: rgba(102, 126, 234, 0.5);
          box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25), 0 8px 25px rgba(0, 0, 0, 0.3);
          transform: translateY(-1px);
        }

        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }

    .btn {
      padding: 0.6rem 1rem;
      font-weight: 500;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
      }
    }
  }

  .bulk-actions {
    display: flex;
    align-items: center;
    gap: 1rem;

    .text-white {
      font-weight: 500;
      font-size: 0.95rem;
    }

    .btn-danger {
      background: linear-gradient(135deg, #dc3545 0%, #bb2d3b 100%);
      border: none;
      padding: 0.4rem 0.5rem;
      font-weight: 500;
      color: #fff;
      border-radius: 8px;
      box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);

      &:hover {
        background: linear-gradient(135deg, #bb2d3b 0%, #a02834 100%);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
      }
    }
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.5);
  }
`

const ColorPickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem;

  .react-colorful {
    width: 280px !important;
    height: 280px !important;

    &__hue {
      border-radius: 0;
    }

    &__saturation {
      border-radius: 50%;
      margin-bottom: 1rem;
    }
  }

  .current-color {
    display: flex;
    align-items: center;
    gap: 1rem;

    .color-preview {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.1);
    }

    .color-value {
      font-family: monospace;
      color: #e0e0e0;
    }
  }
`

// Helper functions for color conversion
const hexToHsl = (hex: string): HslColor => {
  // Remove the hash if it exists
  hex = hex.replace('#', '')

  // Convert hex to RGB
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }

    h /= 6
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

const hslToHex = ({ h, s, l }: HslColor): string => {
  l /= 100
  const a = (s * Math.min(l, 1 - l)) / 100

  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }

  return `#${f(0)}${f(8)}${f(4)}`
}

const DatasetManageTags: React.FC = () => {
  // Local state
  const [tagFilter, setTagFilter] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [showAddTagModal, setShowAddTagModal] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [selectedTagForColor, setSelectedTagForColor] = useState<Tag | null>(null)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [tempColor, setTempColor] = useState<HslColor>({ h: 0, s: 0, l: 0 })
  const [displayColor, setDisplayColor] = useState('#000000')
  const [error, setError] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const addTagInputRef = useRef<HTMLInputElement>(null)

  // Redux selectors
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  // API hooks
  const {
    data: existingTagsData,
    isLoading: isTagsLoading,
    refetch: refetchTags,
  } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const { data: annoStats, refetch: refetchAnnoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    undefined
  )

  const [createTag] = useCreateTagMutation()
  const [deleteTag] = useDeleteTagMutation()
  const [updateTag] = useUpdateTagMutation()

  // Filtered tags with usage count
  const filteredExistingTags = React.useMemo(() => {
    if (!existingTagsData?.tags) return []
    return existingTagsData.tags
      .map((tag) => ({
        ...tag,
        usage_count: annoStats?.statistics?.annotations_per_class?.[tag.id]?.count || 0,
      }))
      .filter((tag) => tag.name.toLowerCase().includes(tagFilter.toLowerCase()))
  }, [existingTagsData?.tags, tagFilter, annoStats?.statistics?.annotations_per_class])

  // Event handlers
  const handleTagSelection = (tagId: string, fromDelete = false) => {
    if (fromDelete) {
      setSelectedTags((prev) => [...prev.filter((id) => id !== tagId), tagId])
      return
    }
    if (tagId === 'all') {
      setSelectedTags(filteredExistingTags.map((tag) => tag.id))
    } else if (tagId === 'none') {
      setSelectedTags([])
    } else {
      setSelectedTags((prev) => {
        if (prev.includes(tagId)) {
          return prev.filter((id) => id !== tagId)
        } else {
          return [...prev, tagId]
        }
      })
    }
  }

  const handleAddTag = async () => {
    if (tagInput.trim()) {
      try {
        // Split by commas and create each tag
        const tagNames = tagInput
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0)

        for (const tagName of tagNames) {
          const newTag = {
            name: tagName,
            color: generateRandomColor().number,
            dataset_id: project?.datasetid || '',
            project_id: selectedProjectId || '',
          }
          await createTag(newTag).unwrap()
        }

        setTagInput('')
        setShowAddTagModal(false)
      } catch (error) {
        setError(
          error
            ? (error as any).data?.message || 'Failed to create tags'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
  }

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const confirmDeleteTags = async () => {
    setShowDeleteConfirmation(false)
    for (const tagId of selectedTags) {
      try {
        await deleteTag({
          tagId,
          projectId: selectedProjectId || '',
          datasetId: project?.datasetid || '',
        })
        await refetchAnnoStats()
        await refetchTags()
      } catch (error) {
        setError(
          error
            ? (error as any).data?.message || 'Failed to delete tag'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
    setSelectedTags([])
  }

  const processContent = useCallback(
    (content: string) => {
      const processedTags = content
        .split(/[,\n]/)
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
        .map((tagName) => ({
          name: tagName,
          color: generateRandomColor().number,
          dataset_id: project?.datasetid || '',
          project_id: selectedProjectId || '',
        }))

      processedTags.forEach(async (tag) => {
        try {
          await createTag(tag).unwrap()
        } catch (error) {
          setError(
            error
              ? (error as any).data?.message || 'Failed to create tag'
              : 'An unexpected error occurred'
          )
          setShowError(true)
        }
      })
    },
    [selectedProjectId, project?.datasetid, createTag]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const content = e.target?.result as string
          processContent(content)
        }
        reader.readAsText(file)
      })
    },
    [processContent]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
    },
    multiple: false,
  })

  const handleColorClick = (tag: Tag) => {
    const hexColor = `#${tag.color.toString(16).padStart(6, '0')}`
    setDisplayColor(hexColor)
    setTempColor(hexToHsl(hexColor))
    setSelectedTagForColor(tag)
    setShowColorPicker(true)
  }

  const handleColorChange = (newColor: HslColor) => {
    setTempColor(newColor)
    setDisplayColor(hslToHex(newColor))
  }

  const handleColorConfirm = async () => {
    if (selectedTagForColor && project?.datasetid) {
      try {
        const colorNumber = parseInt(displayColor.replace('#', ''), 16)
        await updateTag({
          id: selectedTagForColor.id,
          dataset_id: project.datasetid,
          tagData: {
            color: colorNumber,
          },
        }).unwrap()
        setShowColorPicker(false)
        setSelectedTagForColor(null)
      } catch (error) {
        setError(
          error
            ? (error as any).data?.message || 'Failed to update tag color'
            : 'An unexpected error occurred'
        )
        setShowError(true)
      }
    }
  }

  return (
    <>
      <TagManagementContainer>
        <TagContent>
          <div className="header-actions">
            <div className="search-box">
              <Form.Control
                type="text"
                placeholder="Search tags..."
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              />
            </div>

            {selectedTags.length > 0 && (
              <div className="bulk-actions">
                <span className="text-white">
                  {selectedTags.length} tag
                  {selectedTags.length !== 1 ? 's' : ''} selected
                </span>
                <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirmation(true)}>
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Delete Selected
                </Button>
              </div>
            )}
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={() => setShowAddTagModal(true)}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Tag
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                title="Upload tags from file (CSV or TXT)"
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                <FontAwesomeIcon icon={faUpload} className="me-1" />
                Upload
              </Button>
            </div>
          </div>

          {/* {selectedTags.length > 0 && (
            <div className="bulk-actions">
              <span className="text-white">
                {selectedTags.length} tag{selectedTags.length !== 1 ? 's' : ''} selected
              </span>
              <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirmation(true)}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Delete Selected
              </Button>
            </div>
          )} */}

          <Table hover responsive>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleTagSelection('all')
                      } else {
                        handleTagSelection('none')
                      }
                    }}
                    checked={
                      selectedTags.length === filteredExistingTags.length &&
                      filteredExistingTags.length > 0
                    }
                  />
                </th>
                <th>Tag Name</th>
                <th>Color (Click to change)</th>
                <th>Usage Count</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {isTagsLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    <Spinner animation="border" variant="primary" size="sm" />
                  </td>
                </tr>
              ) : filteredExistingTags.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    {tagFilter ? 'No tags match your search' : 'No tags available'}
                  </td>
                </tr>
              ) : (
                filteredExistingTags.map((tag) => (
                  <tr key={tag.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedTags.includes(tag.id)}
                        onChange={() => handleTagSelection(tag.id)}
                      />
                    </td>
                    <td>
                      <span className="tag-name">{tag.name}</span>
                    </td>
                    <td>
                      <span
                        className="color-preview"
                        style={{
                          backgroundColor: `#${tag.color.toString(16).padStart(6, '0')}`,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleColorClick(tag)}
                      ></span>
                      <span className="hex-color">#{tag.color.toString(16).padStart(6, '0')}</span>
                    </td>
                    <td>
                      <Badge bg="secondary">{tag.usage_count} uses</Badge>
                    </td>
                    <td>
                      <span className="date">{new Date(tag.created_at).toLocaleDateString()}</span>
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          handleTagSelection(tag.id, true)
                          setShowDeleteConfirmation(true)
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Add Tag Modal */}
          <Modal
            show={showAddTagModal}
            onHide={() => setShowAddTagModal(false)}
            centered
            contentClassName="bg-dark"
            onEntered={() => addTagInputRef.current?.focus()}
          >
            <Modal.Header closeButton className="border-secondary bg-dark text-white">
              <Modal.Title>Add New Tag</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
              <Form.Group className="mb-3">
                <Form.Label>Tag Name</Form.Label>
                <Form.Control
                  ref={addTagInputRef}
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Enter tag name (e.g. 'cat' or 'cat, dog')"
                  onKeyDown={handleTagInputKeyDown}
                />
                <Form.Text className="text-muted">
                  Enter a single tag or multiple tags separated by commas
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-secondary bg-dark">
              <Button variant="secondary" onClick={() => setShowAddTagModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleAddTag} disabled={!tagInput.trim()}>
                Add Tag
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            show={showDeleteConfirmation}
            onHide={() => setShowDeleteConfirmation(false)}
            centered
            contentClassName="bg-dark"
          >
            <Modal.Header closeButton className="border-secondary bg-dark text-white">
              <Modal.Title>Confirm Tag Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
              <p>
                Are you sure you want to delete {selectedTags.length} selected tag
                {selectedTags.length !== 1 ? 's' : ''}? This action cannot be undone.
              </p>
              <p className="text-danger">
                Warning: Deleting these tags will affect all annotations using them.
              </p>
            </Modal.Body>
            <Modal.Footer className="border-secondary bg-dark">
              <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDeleteTags}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Color Picker Modal */}
          <Modal
            show={showColorPicker}
            onHide={() => setShowColorPicker(false)}
            centered
            contentClassName="bg-dark"
          >
            <Modal.Header closeButton className="border-secondary bg-dark text-white">
              <Modal.Title>Choose Tag Color</Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-dark text-white">
              <ColorPickerContainer>
                <div className="current-color">
                  <div className="color-preview" style={{ backgroundColor: displayColor }} />
                  <span className="color-value">{displayColor}</span>
                </div>
                <HslColorPicker color={tempColor} onChange={handleColorChange} />
              </ColorPickerContainer>
            </Modal.Body>
            <Modal.Footer className="border-secondary bg-dark">
              <Button variant="secondary" onClick={() => setShowColorPicker(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleColorConfirm}>
                Update Color
              </Button>
            </Modal.Footer>
          </Modal>
        </TagContent>
      </TagManagementContainer>

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={error} />
    </>
  )
}

export default DatasetManageTags
